/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  DasScheme,
  StorageKey,
  XFoundationLib,
  Constants,
  ConfigsErrors,
  XError,
  AssetType,
  ImageFormat,
} from '@wit/vtv-x-foundation-lib';
import store from 'store/store';
import { getNetworkType, getSoftwareVersion } from 'store/ducks/analytics/technicalData';
import AppConfig from './AppConfig';
import dasClient from './DasClientWeb';
import PlatformUtils from './utils/PlatformUtils';
import cryptoRsa from './CryptoRsaWeb';

export default class FoundationLibConfig {
  static init(localizedStrings: { [lang: string]: Record<string, string> }): void {
    // Constants
    // override app specific constants
    Constants.TARGET_FAMILY_ID = 5; // PC/MAC
    Constants.TARGET_BRAND_ID = 22; // PC/MAC
    Constants.FEATURE_DMS_HOPS_ON = true;
    Constants.ERROR_CODES_PREFIX = 'W';

    // Device name
    const appName: string | undefined = PlatformUtils.replacePlatformVars(AppConfig.env.DMS_APP_NAME);
    if (appName && PlatformUtils.isSupportedPlatform()) {
      AppConfig.env.DMS_APP_NAME = appName;
    } else {
      throw XError.fromCode(ConfigsErrors.validation.unsupportedPlatform);
    }

    XFoundationLib.init({
      ENV: AppConfig.env,
      localStorage: {
        setItem(key: StorageKey, value: string): Promise<void> {
          return new Promise((resolve) => {
            resolve(localStorage.setItem(key, value));
          });
        },
        getItem(key: StorageKey): Promise<string | null> {
          return new Promise((resolve) => {
            resolve(localStorage.getItem(key));
          });
        },
        removeItem(key: StorageKey): Promise<void> {
          return new Promise((resolve) => {
            resolve(localStorage.removeItem(key));
          });
        },
      },
      localStorageWhitelist: [StorageKey.AGE_RATING, StorageKey.CREDENTIALS],
      store: store(),
      das: {
        dasClient,
        dasScheme: DasScheme.WEB,
      },
      menu: {
        myTv: 'MYTV',
        linearChannels: 'TV GUIDE',
        epgFilters: 'EPG Filters',
        vod: 'FILMS',
        search: 'SEARCH',
        upNext: 'UPNEXT',
        related: 'RELATED',
        mediaLibrary: 'MEDIALIB',
        tlmSideMenu: 'TLMSIDEMENU',
      },
      contentTags: {
        tag: {
          en: { name: 'w_en', defaultValue: false },
          restart: { name: 'w_restart', defaultValue: false },
          live: { name: 'w_live', defaultValue: false },
          catchup: { name: 'w_catchup', defaultValue: false },
          npvr: { name: 'w_npvr', defaultValue: false },
        },
      },
      cryptoRsa,
      localizedStrings,
      assetTypeToImageFormat: {
        [AssetType.PORTRAIT]: ImageFormat.PORTRAIT,
        [AssetType.LANDSCAPE]: ImageFormat.LANDSCAPE,
        [AssetType.WIDE]: ImageFormat.WIDE,
        [AssetType.CHANNEL]: ImageFormat.CHANNEL,
        [AssetType.SQUARE]: ImageFormat.SQUARE,
      },
      bookmarksPersistenceEnabled: true,
      systemInfo: {
        softwareVersion: getSoftwareVersion(),
        appVersion: AppConfig.env.VERSION,
        networkType: getNetworkType,
      },
    });
  }
}
